import type { BoxProps } from "@mui/material";
import { Alert, AlertTitle, Box, LinearProgress, Typography } from "@mui/material";
import { memo, useMemo, useRef } from "react";
import { Carousel } from "../discover/carousel/carousel";
import { CeHelpButton } from "../generic/components/ce-help-button";
import { useAccount } from "../generic/hooks/use-account";
import { MomentCard } from "../moment-card/components/moment-card";
import type { MomentCardMomentActivity } from "../moment/server/supabase-moment-service";

interface MomentDashboardSectionProps extends BoxProps {
  title: string;
  help?: string;
  moments?: MomentCardMomentActivity[];
  hasErrors?: boolean;
  hideTitle?: boolean;
}

export const MomentDashboardSection = memo(function MomentDashboardSectionFn({
  title,
  moments,
  hasErrors,
  help,
  hideTitle = false,
  ...boxProps
}: MomentDashboardSectionProps) {
  const { data: account } = useAccount();

  const ref = useRef<HTMLElement>(null);

  const items = useMemo(
    () => moments?.map((value, key) => ({ key: key.toString(), value })),
    [moments],
  );

  if (!account) return null;

  return (
    <Box
      data-testid={`dashboard-section-${title.replace(" ", "-").toLowerCase()}`}
      {...boxProps}
      sx={{ ...boxProps?.sx }}
      ref={ref}
    >
      {!hideTitle && (
        <Typography variant="h5" sx={{ mb: 3, px: 2 }}>
          {title}
          {help && <CeHelpButton content={help} buttonSx={{ mb: 0.5 }} />}
        </Typography>
      )}
      {hasErrors && (
        <Alert severity="error">
          <AlertTitle>Something went wrong</AlertTitle>
        </Alert>
      )}
      {items === undefined ? (
        <LinearProgress />
      ) : items.length === 0 ? (
        <Alert severity="info">
          <AlertTitle>{`No ${title} Communications`}</AlertTitle>
        </Alert>
      ) : (
        <Carousel
          items={items}
          gap={2}
          Item={({ value }) => (
            <MomentCard draggable={true} moment={value.value} account={account} />
          )}
          itemWidth="calc(max(min(330px, 20%), 300px))"
        />
      )}
    </Box>
  );
});
